import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, LogOut, Settings, ChevronDown, Crown } from 'lucide-react';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import { useLocation } from 'wouter';
import { FoundersCircleBadge } from './badges/FoundersCircleBadge';
import { VERSION, COMMIT_ID } from '../version';

export const UserProfile: React.FC = () => {
  const { user, signOut, userRole, foundersCircle } = useSupabaseAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [, setLocation] = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    if (isSigningOut) return; // Prevent double-clicks

    console.log('[UserProfile] 🔐 Starting sign out process...');
    setIsSigningOut(true);
    setIsOpen(false); // Close dropdown immediately

    try {
      // Sign out from Supabase (context now handles all clearing)
      console.log('[UserProfile] 📤 Calling Supabase signOut...');
      await signOut();
      console.log('[UserProfile] ✅ Sign out completed, redirecting...');
    } catch (error) {
      console.error('[UserProfile] ⚠️ Sign out error (will redirect anyway):', error);
    } finally {
      // Always redirect to home with full page reload
      console.log('[UserProfile] 🏠 Redirecting to home...');
      // Use a small delay to ensure state updates have propagated
      setTimeout(() => {
        window.location.replace('/');
      }, 150);
    }
  };

  if (!user) return null;

  const displayName = user.user_metadata?.firstName || user.email?.split('@')[0] || 'User';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
      >
        <div className={`relative ${foundersCircle ? 'pr-1' : ''}`}>
          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
            <User className="w-5 h-5 text-white" />
          </div>
          {foundersCircle && (
            <div className="absolute -top-1 -right-1">
              <FoundersCircleBadge size="sm" />
            </div>
          )}
        </div>
        <span className="text-white text-sm font-medium whitespace-nowrap">{displayName}</span>
        <ChevronDown className={`w-4 h-4 text-white/70 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute right-0 mt-2 w-[min(16rem,calc(100vw-2rem))] bg-black/90 backdrop-blur-xl rounded-lg shadow-xl border border-white/10 overflow-hidden z-dropdown"
          >
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <p className="text-white font-medium truncate">{displayName}</p>
                {foundersCircle && <FoundersCircleBadge size="sm" />}
              </div>
              <p className="text-white/60 text-sm truncate">{user.email}</p>
            </div>

            <div className="p-2">
              {userRole === 'admin' && (
                <button
                  onClick={() => {
                    setIsOpen(false);
                    setLocation('/admin');
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 min-h-[44px] rounded hover:bg-white/10 transition-colors touch-manipulation"
                >
                  <Crown className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                  <span className="text-white/90 text-sm">Admin Panel</span>
                </button>
              )}

              <button
                onClick={() => {
                  setIsOpen(false);
                  setLocation('/settings');
                }}
                className="w-full flex items-center gap-3 px-3 py-2 min-h-[44px] rounded hover:bg-white/10 transition-colors touch-manipulation"
              >
                <Settings className="w-4 h-4 text-white/70 flex-shrink-0" />
                <span className="text-white/90 text-sm">Settings</span>
              </button>

              <button
                onClick={handleSignOut}
                onMouseDown={(e) => e.stopPropagation()}
                disabled={isSigningOut}
                className="w-full flex items-center gap-3 px-3 py-2 min-h-[44px] rounded hover:bg-white/10 transition-colors text-red-400 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
              >
                <LogOut className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">{isSigningOut ? 'Signing out...' : 'Sign Out'}</span>
              </button>
            </div>

            <div className="px-4 py-3 bg-white/5 border-t border-white/10">
              <div className="text-xs text-white/50 mb-1">
                {user.app_metadata?.provider === 'email' ? 'Email Login' :
                 user.user_metadata?.migrated_from ? `Migrated from ${user.user_metadata.migrated_from}` :
                 'Hover Account'}
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-white/40">Version</span>
                <span className="text-white/60 font-mono">
                  v{VERSION} ({COMMIT_ID})
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};